import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, forkJoin, Subject } from 'rxjs';
import { AssessmentEdit } from 'src/app/assessment/model/assessment.edit';
import { Cacheable, CacheBuster } from 'ts-cacheable';
import { AssessmentType } from '../model/assessmentType';
import { AssessmentTypeList, AssessmentTypeEdit } from 'src/app/assessment/model/assessment-type.edit';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { UntypedFormGroup } from '@angular/forms';
import { merge } from 'lodash';

const assessmentTypesCacheBuster = new Subject<void>();

@Injectable({
  providedIn: 'root'
})
export class AssessmentService
{
  private flatFields = [];

  constructor(private http: HttpClient) { }

  /**
   * Get all cached Assessment Types
   */
  @Cacheable(
    {
      cacheBusterObserver: assessmentTypesCacheBuster
    })
  public getCachedAssessmentTypes(): Observable<AssessmentType[]>
  {
    return this.http.get<AssessmentType[]>(`/assessment/types`);
  }

  /**
   * Delete an Assessment Type.
   *
   * @param id Assessment Type id
   */
  @CacheBuster(
    {
      cacheBusterNotifier: assessmentTypesCacheBuster
    })
  public deleteAssessmentType(id: number): Observable<AssessmentTypeList>
  {
    return this.http.delete<AssessmentTypeList>(`/assessment/type/${id}`);
  }

  /**
   * Save an Assessement Type.
   *
   * @param data Assessment Type to persit
   * @param isNew Indicate if this is a new or update
   */
  @CacheBuster(
    {
      cacheBusterNotifier: assessmentTypesCacheBuster
    })
  public saveAssessmentType(data: any, isNew: boolean, assessmentTypeId?: number): Observable<any>
  {
    if (isNew)
    {
      return this.http.post('/assessment/type', data);
    }
    else
    {
      return this.http.put(`/assessment/type/${assessmentTypeId}`, data);
    }
  }

  /**
   * Get Assessment by id.
   *
   * @param id Assessment id
   */
  public getAssessment(id: number): Observable<AssessmentEdit>
  {
    return this.http.get<AssessmentEdit>(`/assessment/${id}`);
  }

  /**
   * Delete an assessment.
   *
   * @param id Assessment id
   */
  public deleteAssessment(id: number): Observable<AssessmentEdit>
  {
    return this.http.delete<AssessmentEdit>(`/assessment/${id}`);
  }

  /**
   * Save an Assessment.
   *
   * @param data Assessment to persit
   * @param isNew Indicate if this is a new or update
   */
  public saveAssessment(data: any, isNew: boolean, assessmentId?: number): Observable<any>
  {
    if (isNew)
    {
      return this.http.post('/assessment', data);
    }
    else
    {
      return this.http.put(`/assessment/${assessmentId}`, data);
    }
  }

  /**
   * Save an Assessment fulfillment.
   *
   * @param data Assessment fulfillment to persit
   */
  public saveAssessmentFulfillment(data: any): Observable<any>
  {
    return this.http.post('/assessment/fulfillment', data);
  }

  /**
   * Update an Assessment fulfillment.
   *
   * @param data Assessment fulfillment to persit
   */
  public updateAssessmentFulfillment(data: any): Observable<any>
  {
    return this.http.put('/assessment/fulfillment', data);
  }

  /**
   * Get Assessment Types.
   */
  public getAssessmentTypesList(): Observable<AssessmentTypeList[]>
  {
    return this.http.get<AssessmentTypeList[]>('/assessment/types/list');
  }

  /**
   * Check if assessment is in use by the Modeler
   *
   * @param data Assessment Id
   */
  public inUseByModeler(id: number): Observable<any>
  {
    return this.http.get(`/assessment/inuse/${id}`);
  }

  /**
   * Get assessment versions by assessment id.
   *
   * @param id Assessment id
   */
  public getAssessmentVersions(id: number): Observable<string[]>
  {
    return this.http.get<string[]>(`/assessment/${id}/versions`);
  }

  /**
   * Get all the assessments completed for a specific version and date range.
   *
   * @param version Assessment version
   * @param fromDate Assessment From Date
   * @param toDate Assessment To Date
   */
  public getCompletedAssessments(assessmentId: number, version: string, fromDate?: Date, toDate?: Date): Observable<any[]>
  {
    const paramsArray = [];
    let params = '';

    paramsArray.push(`version=${version}`);

    if (fromDate !== undefined)
    {
      paramsArray.push(`from=${fromDate.toISOString().replace('Z', '')}`);
    }

    if (toDate !== undefined)
    {
      paramsArray.push(`to=${toDate.toISOString().replace('Z', '')}`);
    }

    if (paramsArray.length > 0)
    {
      params = `?${paramsArray.join('&')}`;
    }
    return this.http.get<any[]>(`/assessment/${assessmentId}/completed${params}`);
  }

  public getCompletedAssessmentsFromMultipleSources(versions: Array<string>, assessmentId: number,
    fromDate?: Date, toDate?: Date): Observable<any>
  {
    const listQueries: any = [];
    versions.forEach(
      version =>
      {
        listQueries.push(this.getCompletedAssessments(assessmentId, version, fromDate, toDate));
      });
    return forkJoin(listQueries);
  }

  /**
   * Check if Assessment Type is in use
   *
   * @param id Assessment Type Id
   */
  public existsAssessmentType(id: number): Observable<any>
  {
    return this.http.get(`/assessment/type/inuse/${id}`);
  }

  /**
   * Get Assessment Type by id.
   *
   * @param id Assessment Type id
   */
  public getAssessmentTypeById(id: number): Observable<AssessmentTypeEdit>
  {
    return this.http.get<AssessmentTypeEdit>(`/assessment/type/${id}`);
  }

  /**
   * Check if Assessment Type Name exists to avoid name duplicates
   *
   * @param name Assessment Type Name
   */
  public existsAssessmentTypeName(name: string): Observable<any>
  {
    return this.http.get(`/assessment/type/exists?name=${name}`);
  }

  /**
   * Delete Assessment fulfillment.
   *
   * @param assessmentFulfillmentId Assessment fulfillment id.
   */
  public deleteAssessmentFulfillment(assessmentFulfillmentId: number): Observable<any>
  {
    return this.http.delete(`/assessment/fulfillment/${assessmentFulfillmentId}`);
  }

  /**
   * This method process the assessment fields and return a flat list of fields.
   * 
   * @param fields Assessment fields
   * @returns Assessment arrays of fields
   */
  public getFlatAssessmentFields(fields: FormlyFieldConfig[]): any[] 
  {
    this.flatFields = [];
    fields.forEach((f: any) => { this.processAssessmentField(f); });
    return this.flatFields;
  }

  public setFormlyControlsSum(fields: any, form: any): void
  {
    // First we need to create all controls that contains sum
    const sumKeys = [];
    fields.forEach((f: any) => { this.processAssessmentFieldSum(f, form, sumKeys); });

    // If we have sum, we need to add valueChanges events
    if (sumKeys.length > 0)
    {
      for (const key of Object.keys(form.controls))
      {
        this.processFormlyFormControls(sumKeys, form.controls[key], form.controls);
      }
    }
  }

  /**
   * This method set the model default for date and date/time
   * @param fields Formly fields
   * @param model Model object
   */
  public setFormlyModelDefaults(fields: any, model: any): void 
  {
    fields.forEach((f: any) => { this.processFormlyModelDefaults(model, f, null); });
  }

  /**
   * Get all Assessments.
   */
  public getAllAssessmentMinimal(): Observable<any>
  {
    return this.http.get<any>('/assessments/minimal');
  }

  /**
   * This recursive function extract all fields including all controls in all tabs (fieldGroup)
   * 
   * @param f Field record to process.
   * @param parentTabKey Parent tab key name used to set model if the date or datetime control is in a tab
   */
  private processFormlyModelDefaults(model: any, f: any, parentTabKey: string): void 
  {
    if (f.fieldGroup) 
    {
      f.fieldGroup.forEach((t: any) => { this.processFormlyModelDefaults(model, t, f?.type === 'tabs' ? f.key : parentTabKey); });
    }
    else if (f.key && f.templateOptions && (f.type === 'date' || f.type === 'datetime') && f.templateOptions.defaultToday)
    {
      const now = new Date();
      const dateValue: Date = f.type === 'datetime' ? now : new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);

      // Is control within a tab?
      if (parentTabKey && f.tab)
      {
        if (model[parentTabKey] === undefined) { model[parentTabKey] = {}; }
        model[parentTabKey][f.tab] = merge(model[parentTabKey][f.tab], { [f.key]: dateValue });
      }
      else
      {
        model = merge(model, { [f.key]: dateValue });
      }
    }
  }

  /**
   * This recursive function extract all fields including all controls in all tabs (fieldGroup)
   * 
   * @param f Field record to process.
   */
  private processAssessmentField(f: any): void 
  {
    if (f.fieldGroup) 
    {
      f.fieldGroup.forEach((t: any) => { this.processAssessmentField(t); });
    }
    else if (f.key)
    {
      this.flatFields.push(f);
    }
  }

  private processFormlyFormControls(sumKeys: any, form: any, formControls: any): void 
  {
    if (form.controls) 
    {
      for (const key of Object.keys(form.controls)) 
      {
        this.addFormlyValueChange(sumKeys, key, form);
        this.processFormlyFormControls(sumKeys, form.controls[key], formControls);
      }
    }
    else 
    {
      for (const key of Object.keys(formControls)) 
      {
        this.addFormlyValueChange(sumKeys, key, formControls);
      }
    }
  }

  private addFormlyValueChange(sumKeys: any, key: string, form: UntypedFormGroup): void 
  {
    let foundKey = null;
    sumKeys.forEach((x: any) => { if (x.fields.includes(key)) { foundKey = x; } });
    if (foundKey) 
    {
      const formControls = form.controls ? form.controls : form;
      formControls[key].valueChanges.subscribe(() =>
      {
        let sumTotal = 0;
        // Use replace to keep only numeric within value (Example: Frequently5 = will sum as 5)
        foundKey.fields.forEach((x: any) =>
        {
          if (typeof formControls[x].value === 'number')
          {
            sumTotal += (formControls[x].value ? formControls[x].value : 0) | 0;
          }
          else
          {
            sumTotal += (formControls[x].value ? +formControls[x].value.replace(/\D/g, '') : null) | 0;
          }
        });
        formControls[foundKey.sumKey].setValue(sumTotal);
      });
    }
  }

  /**
   * This recursive function extract all fields including all controls in all tabs (fieldGroup)
   * 
   * @param f Field record to process.
   */
  private processAssessmentFieldSum(f: any, form: any, sumKeys: any): void 
  {
    if (f.fieldGroup) 
    {
      f.fieldGroup.forEach((t: any) => { this.processAssessmentFieldSum(t, form, sumKeys); });
    }
    else if (f.key)
    {
      if (f && f.templateOptions && f.templateOptions.sumKey) 
      {
        const foundSum = sumKeys.find((x: any) => x.sumKey === f.templateOptions.sumKey);
        if (!foundSum) 
        {
          sumKeys.push({ sumKey: f.templateOptions.sumKey, fields: [f.key] });
        }
        else
        {
          foundSum.fields.push(f.key);
        }
      }
    }
  }
}
